// ── Standard card face ───────────────────────────────────────────────────────
// The shared, traditional 52-card face used by every theme: corner indices, standard pip layouts
// for 2–10, a single large Ace pip, and court-card artwork (shared deck or a theme's own) with a
// framed fallback until that artwork is supplied. Returns HTML so the same face renders in the
// hand, on the table, in the showdown and in previews. Styling: .cf-* rules in index.html.

import type { Card } from "../../engine/cards.ts";
import { courtImage } from "../themes/apply.ts";
import type { CourtKey } from "../themes/registry.ts";

const GLYPH: Record<string, string> = { C: "♣", D: "♦", H: "♥", S: "♠" };
const RANK: Record<number, string> = { 11: "J", 12: "Q", 13: "K", 14: "A" };

// Pip positions (x, y as % of the pip area). Pips in the lower half are drawn upside down,
// as on a real card, so the face reads the same either way up.
const PIPS: Record<number, [number, number][]> = {
  2: [[50, 0], [50, 100]],
  3: [[50, 0], [50, 50], [50, 100]],
  4: [[0, 0], [100, 0], [0, 100], [100, 100]],
  5: [[0, 0], [100, 0], [50, 50], [0, 100], [100, 100]],
  6: [[0, 0], [100, 0], [0, 50], [100, 50], [0, 100], [100, 100]],
  7: [[0, 0], [100, 0], [50, 25], [0, 50], [100, 50], [0, 100], [100, 100]],
  8: [[0, 0], [100, 0], [50, 25], [0, 50], [100, 50], [50, 75], [0, 100], [100, 100]],
  9: [[0, 0], [100, 0], [0, 33.3], [100, 33.3], [50, 50], [0, 66.7], [100, 66.7], [0, 100], [100, 100]],
  10: [[0, 0], [100, 0], [50, 16.7], [0, 33.3], [100, 33.3], [0, 66.7], [100, 66.7], [50, 83.3], [0, 100], [100, 100]],
};

export function faceHtml(c: Card): string {
  const r = RANK[c.rank] ?? String(c.rank), g = GLYPH[c.suit];
  const idx = `<span class="cf-r${c.rank === 10 ? " ten" : ""}">${r}</span><span class="cf-s">${g}</span>`;
  let mid: string;
  if (c.rank === 14) mid = `<div class="cf-ace"><span>${g}</span></div>`;
  else if (c.rank >= 11) {
    const img = courtImage(`${r}${c.suit}` as CourtKey);
    mid = img
      ? `<div class="cf-court"><img src="${img}" alt="" draggable="false"></div>`
      : `<div class="cf-court cf-court-fallback"><span class="cf-cr">${r}</span><span class="cf-cs">${g}</span></div>`;
  } else {
    mid = `<div class="cf-pips">${PIPS[c.rank].map(([x, y]) =>
      `<span class="${y > 50 ? "flip" : ""}" style="left:${x}%;top:${y}%">${g}</span>`).join("")}</div>`;
  }
  return `<div class="cf-idx tl">${idx}</div>${mid}<div class="cf-idx br">${idx}</div>`;
}

/** A plain text card ("9♥", no pips or pictures) for places where a full face is too small to read:
 *  the phone's score-details table. Styling: .sd2-chip in index.html. */
export function chipHtml(c: Card): string {
  const r = RANK[c.rank] ?? String(c.rank), red = c.suit === "H" || c.suit === "D";
  return `<div class="sd2-chip${red ? " red" : ""}" aria-label="${r} of ${({ C: "clubs", D: "diamonds", H: "hearts", S: "spades" } as Record<string, string>)[c.suit]}">${r}<span>${GLYPH[c.suit]}</span></div>`;
}
